import type { CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';

// Define type based on our Prisma schema
export interface HotelAccessCode {
  id: string;
  name: string;
  description?: string;
  code: string;
  hotelId: string;
  durationInHours?: number;
  isActive: boolean;
  expiresAt?: string;
  revokedAt?: string;
  createdById?: string;
  updatedById?: string;
  createdAt: string;
  updatedAt: string;
  // Relations for display
  hotel?: {
    id: string;
    name: string;
    city: string;
    state: string;
  };
  createdBy?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  updatedBy?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
}

/**
 * Hotel Access Code CRUD configuration
 */
export const hotelAccessCodeCrudConfig: CrudConfig<HotelAccessCode> = {
  entityName: 'Access Code',
  entityNamePlural: 'Access Codes',

  endpoints: {
    list: '/hotel-access-codes',
    single: (id: string) => `/hotel-access-codes/${id}`,
    create: '/hotel-access-codes',
    update: (id: string) => `/hotel-access-codes/${id}`,
    delete: (id: string) => `/hotel-access-codes/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter access code name',
      validation: {
        min: 1,
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'code',
      label: 'Code',
      type: 'text',
      required: true,
      placeholder: 'Enter access code',
    },
    {
      key: 'hotel',
      label: 'Hotel',
      type: 'text',
      required: false,
      readonly: true,
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/hotels/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'durationInHours',
      label: 'Duration (Hours)',
      type: 'number',
      required: false,
      placeholder: 'Duration in hours',
      validation: {
        min: 0,
      },
    },
    {
      key: 'isActive',
      label: 'Is Active',
      type: 'boolean',
      required: false,
      defaultValue: true,
    },
    {
      key: 'expiresAt',
      label: 'Expires At',
      type: 'datetime',
      required: false,
    },
    {
      key: 'revokedAt',
      label: 'Revoked At',
      type: 'datetime',
      required: false,
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['name', 'code', 'hotel', 'isActive', 'expiresAt', 'createdAt'],
    searchableFields: ['name', 'code'],
    sortableFields: ['name', 'code', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    filters: [
      {
        key: 'isActive',
        label: 'Is Active',
        type: 'select',
        options: [
          { label: 'Active', value: 'true' },
          { label: 'Inactive', value: 'false' },
        ],
      },
    ],
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core access code details',
        fields: ['name', 'description', 'code', 'hotel', 'isActive'],
        defaultExpanded: true,
      },
      {
        title: 'Validity',
        description: 'Access code validity information',
        fields: ['durationInHours', 'expiresAt', 'revokedAt'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Access Code',
        fields: ['name', 'description', 'code', 'isActive'],
        columns: 1,
      },
      {
        title: 'Validity',
        description: 'Validity Information',
        fields: ['durationInHours', 'expiresAt'],
        columns: 2,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 1,
        max: 200,
      },
      code: {
        required: true,
      },
    },
  },
};
